import manifest from "@/data/cameraImagesManifest.json";

const IMAGE_BASE_PATH = "/camera-images";

type ManifestShape = {
  models: Record<string, string>;
  byVendor: Record<string, Record<string, string>>;
};

const typedManifest = manifest as ManifestShape;

const normalize = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, "");

const slugCache = new Map<string, string | null>();

function findSlug(modelName: string, manufacturer?: string): string | null {
  const cacheKey = `${manufacturer ?? ""}|${modelName}`;
  if (slugCache.has(cacheKey)) return slugCache.get(cacheKey) ?? null;

  // 1. Exact PTP-name match (most reliable)
  const exact = typedManifest.models[modelName];
  if (exact) {
    slugCache.set(cacheKey, exact);
    return exact;
  }

  // 2. Vendor + model exact match in byVendor index
  if (manufacturer) {
    const vendorEntries = typedManifest.byVendor[manufacturer];
    if (vendorEntries && vendorEntries[modelName]) {
      const slug = vendorEntries[modelName];
      slugCache.set(cacheKey, slug);
      return slug;
    }
  }

  // 3. Fuzzy: normalize and search across all manifest entries
  const normalizedQuery = normalize(modelName);
  if (normalizedQuery.length >= 3) {
    for (const [ptpName, slug] of Object.entries(typedManifest.models)) {
      const normalizedPtp = normalize(ptpName);
      if (normalizedPtp.includes(normalizedQuery) || normalizedQuery.includes(normalizedPtp)) {
        slugCache.set(cacheKey, slug);
        return slug;
      }
    }
    for (const [vendor, models] of Object.entries(typedManifest.byVendor)) {
      if (manufacturer && normalize(vendor) !== normalize(manufacturer)) continue;
      for (const [model, slug] of Object.entries(models)) {
        if (normalize(model) === normalizedQuery) {
          slugCache.set(cacheKey, slug);
          return slug;
        }
      }
    }
  }

  slugCache.set(cacheKey, null);
  return null;
}

export interface CameraImageLookup {
  url: string | null;
  slug: string | null;
  hasImage: boolean;
}

export function getCameraImage(
  modelName: string | undefined | null,
  manufacturer?: string | null,
): CameraImageLookup {
  if (!modelName) return { url: null, slug: null, hasImage: false };
  const slug = findSlug(modelName, manufacturer ?? undefined);
  if (!slug) return { url: null, slug: null, hasImage: false };
  return {
    url: `${IMAGE_BASE_PATH}/${slug}.webp`,
    slug,
    hasImage: true,
  };
}
